"use client"

import { useState, useEffect, useCallback } from "react"
import { GlowCard } from "@/components/crystality/glow-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { CreditCard, Shield, Clock, Bitcoin, ChevronLeft } from "lucide-react"
import { cn } from "@/lib/utils"
import { trackConversion } from "@/lib/analytics"
import { CryptoCheckout } from "./crypto-checkout"
import { MollieCheckout } from "./mollie-checkout"

interface CheckoutStepProps {
  initialEmail?: string
  onComplete: (email: string, licenseKey: string, orderId?: string) => void
  onError: () => void
}

// Prices in GBP — matching MOLLIE_PRODUCTS in lib/mollie.ts
const plans = [
  {
    id: "weekly",
    name: "Weekly Plan",
    price: 24.99,
    originalPrice: 34.99,
    period: "Weekly",
    popular: false,
  },
  {
    id: "monthly",
    name: "Monthly Plan",
    price: 29.99,
    originalPrice: 39.99,
    period: "Monthly",
    popular: true,
  },
  {
    id: "3month",
    name: "3-Month Plan",
    price: 74.99,
    originalPrice: 89.97,
    period: "Every 3 Months",
    popular: false,
  },
  {
    id: "lifetime",
    name: "Lifetime Plan",
    price: 399.99,
    originalPrice: null,
    period: "One-Time",
    popular: false,
  },
]

// Crypto fee in GBP (conversion overhead)
const CRYPTO_FEE_GBP = 5

type PaymentMethod = "crypto" | "card"

interface PaymentMethodOption {
  id: PaymentMethod
  name: string
  icon: React.ReactNode
  available: boolean
  description: string
}

type CheckoutStep = "plan" | "payment-method" | "crypto-checkout" | "card-checkout"

export function CheckoutStep({ initialEmail = "", onComplete, onError }: CheckoutStepProps) {
  const [email, setEmail] = useState(initialEmail)
  const [selectedPlan, setSelectedPlan] = useState("monthly")
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null)
  const [currentStep, setCurrentStep] = useState<CheckoutStep>("plan")
  const [error, setError] = useState("")
  const [isSignedIn, setIsSignedIn] = useState(false)
  const [referralCode, setReferralCode] = useState("")
  const [showReferral, setShowReferral] = useState(false)

  const paymentMethods: PaymentMethodOption[] = [
    {
      id: "card",
      name: "Card / Apple Pay / Google Pay / PayPal",
      icon: <CreditCard className="h-5 w-5" />,
      available: true,
      description: "Visa, Mastercard, Amex, Apple Pay, Google Pay, PayPal & local methods",
    },
    {
      id: "crypto",
      name: "Cryptocurrency",
      icon: <Bitcoin className="h-5 w-5" />,
      available: true,
      description: `Pay with BTC, ETH, LTC & 150+ coins (+£${CRYPTO_FEE_GBP} fee)`,
    },
  ]

  const getPrice = (planId: string, method: PaymentMethod | null): number => {
    const plan = plans.find((p) => p.id === planId)
    if (!plan) return 0
    if (method === "crypto") return plan.price + CRYPTO_FEE_GBP
    return plan.price
  }

  const formatPrice = (amount: number) => `£${amount.toFixed(2)}`

  // Check sign-in state
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch("/api/auth/me")
        if (response.ok) {
          const data = await response.json()
          if (data.success && data.user) setIsSignedIn(true)
        }
      } catch {}
    }
    checkAuth()
  }, [])

  // Sync email from props (reactivating users)
  useEffect(() => {
    if (initialEmail) setEmail(initialEmail)
  }, [initialEmail])

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleContinueToPaymentMethod = () => {
    if (!email) { setError("Please enter your email address"); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Please enter a valid email address"); return }
    setError("")
    // Track checkout intent for abandoned checkout follow-ups
    fetch("/api/checkout/track-intent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.toLowerCase().trim(), planId: selectedPlan }),
    }).catch(() => {})
    trackConversion("checkout_started", { planId: selectedPlan, email, paymentMethod: "card" })
    setCurrentStep("card-checkout")
  }

  const handleBackToPlanSelection = () => {
    setCurrentStep("plan")
    setSelectedPaymentMethod(null)
    setError("")
  }

  const handleSelectPaymentMethod = (method: PaymentMethod) => {
    const option = paymentMethods.find((p) => p.id === method)
    if (!option?.available) return
    setSelectedPaymentMethod(method)
    setError("")
    trackConversion("checkout_started", { planId: selectedPlan, email, paymentMethod: method })
    if (method === "crypto") setCurrentStep("crypto-checkout")
    else if (method === "card") setCurrentStep("card-checkout")
  }

  const handleCheckoutCancel = () => {
    setCurrentStep("payment-method")
    setSelectedPaymentMethod(null)
  }

  const handleCheckoutError = (errorMsg: string) => {
    setError(errorMsg)
    onError()
  }

  const handleComplete = useCallback(async (licenseKey: string, orderId: string) => {
    await trackConversion("checkout_completed", {
      planId: selectedPlan, email, orderId, paymentMethod: selectedPaymentMethod,
    })

    await fetch("/api/get-started/progress", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        step: "account",
        data: { email, planId: selectedPlan, orderId, licenseKey },
      }),
    }).catch(() => {})

    onComplete(email, licenseKey, orderId)
  }, [email, selectedPlan, selectedPaymentMethod, onComplete])

  // ── Card Checkout (Mollie) ────────────────────────────────────────────

  if (currentStep === "card-checkout") {
    const selectedPlanData = plans.find((p) => p.id === selectedPlan)
    return (
      <MollieCheckout
        email={email}
        planId={selectedPlan}
        planName={selectedPlanData?.name || "Subscription"}
        priceGbp={getPrice(selectedPlan, "card")}
        referralCode={referralCode || undefined}
        onComplete={handleComplete}
        onCancel={handleCheckoutCancel}
        onError={handleCheckoutError}
        onSwitchToCrypto={() => {
          setSelectedPaymentMethod("crypto")
          setCurrentStep("crypto-checkout")
        }}
      />
    )
  }

  // ── Crypto Checkout ───────────────────────────────────────────────────

  if (currentStep === "crypto-checkout") {
    const selectedPlanData = plans.find((p) => p.id === selectedPlan)
    return (
      <CryptoCheckout
        email={email}
        planId={selectedPlan}
        planName={selectedPlanData?.name || "Subscription"}
        priceGbp={getPrice(selectedPlan, "crypto")}
        referralCode={referralCode || undefined}
        onComplete={handleComplete}
        onCancel={handleCheckoutCancel}
        onError={handleCheckoutError}
      />
    )
  }

  // ── Payment Method Selection ──────────────────────────────────────────

  if (currentStep === "payment-method") {
    const selectedPlanData = plans.find((p) => p.id === selectedPlan)
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Choose Payment Method</h1>
          <p className="mt-2 text-muted-foreground">
            Select how you'd like to pay for your subscription
          </p>
        </div>

        {/* Order Summary */}
        <GlowCard variant="subtle" className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{selectedPlanData?.name}</p>
              <p className="text-xs text-muted-foreground">{email}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={handleBackToPlanSelection}
            >
              <ChevronLeft className="h-3 w-3 mr-1" /> Change
            </Button>
          </div>
        </GlowCard>

        {/* Payment Methods */}
        <div className="grid gap-3">
          {paymentMethods.map((method) => {
            const price = getPrice(selectedPlan, method.id)
            return (
              <GlowCard
                key={method.id}
                variant="default"
                glow={selectedPaymentMethod === method.id}
                hover
                className={cn(
                  "p-4 cursor-pointer transition-all",
                  selectedPaymentMethod === method.id && "border-primary"
                )}
                onClick={() => handleSelectPaymentMethod(method.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-primary/20 text-primary">
                    {method.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{method.name}</span>
                    <p className="text-xs text-muted-foreground">{method.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold">{formatPrice(price)}</span>
                    {method.id === "crypto" && (
                      <p className="text-xs text-muted-foreground">+£{CRYPTO_FEE_GBP} fee</p>
                    )}
                  </div>
                </div>
              </GlowCard>
            )
          })}
        </div>

        {error && <p className="text-sm text-destructive text-center">{error}</p>}

        <Button variant="outline" className="w-full" onClick={handleBackToPlanSelection}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back to Plan Selection
        </Button>

        <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /><span>Secure Payment</span></div>
          <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /><span>Instant Access</span></div>
        </div>
      </div>
    )
  }

  // ── Plan Selection ─────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Choose Your Plan</h1>
        <p className="mt-2 text-muted-foreground">
          Select a subscription and enter your email to continue
        </p>
      </div>

      {/* Plan Selection */}
      <div className="grid gap-3">
        {plans.map((plan) => (
          <GlowCard
            key={plan.id}
            variant={plan.popular ? "featured" : "default"}
            glow={selectedPlan === plan.id}
            hover
            className={cn("cursor-pointer p-4 transition-all", selectedPlan === plan.id && "border-primary")}
            onClick={() => setSelectedPlan(plan.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                    selectedPlan === plan.id ? "border-primary bg-primary" : "border-muted-foreground"
                  )}
                >
                  {selectedPlan === plan.id && <div className="w-2 h-2 rounded-full bg-primary-foreground" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{plan.name}</span>
                    {plan.popular && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary text-primary-foreground">
                        Most Popular
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{plan.period}</p>
                </div>
              </div>
              <div className="text-right">
                {plan.originalPrice && (
                  <span className="text-sm text-muted-foreground line-through mr-2">
                    {formatPrice(plan.originalPrice)}
                  </span>
                )}
                <span className="text-xl font-bold">{formatPrice(plan.price)}</span>
              </div>
            </div>
          </GlowCard>
        ))}
      </div>

      {/* Email Input */}
      <div className="space-y-2">
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={cn(
            "bg-secondary/50 border-border focus:border-primary",
            isSignedIn && "bg-secondary/30 text-muted-foreground cursor-not-allowed"
          )}
          disabled={isSignedIn}
          readOnly={isSignedIn}
        />
        <p className="text-xs text-muted-foreground">Your license key will be sent to this email</p>
      </div>

      {/* Referral Code */}
      <div>
        {!showReferral ? (
          <button
            type="button"
            onClick={() => setShowReferral(true)}
            className="text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            Have a referral code?
          </button>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="referral" className="text-sm">Referral Code <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              id="referral"
              type="text"
              placeholder="Enter referral code"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))}
              className="bg-secondary/50 border-border focus:border-primary"
              maxLength={24}
            />
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive text-center">{error}</p>}

      <Button
        className="w-full h-12 gradient-purple hover:opacity-90 transition-opacity text-white font-medium"
        onClick={handleContinueToPaymentMethod}
      >
        Continue to Checkout
      </Button>

      <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /><span>Secure Payment</span></div>
        <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /><span>Instant Access</span></div>
      </div>
    </div>
  )
}
